import { Disable2FAModal } from '@/components/profile/Disable2FAModal'
import { EditPasswordModal } from '@/components/profile/EditPasswordModal'
import { CUSTOMER_TYPE, IndividualType } from '@/constants/account'
import { ROUTES } from '@/constants/routes'
import { queryKeys } from '@/lib/query-keys'
import { formatExpiration, formatFullName } from '@/lib/utils'
import { organizationsService } from '@/services/organizations'
import { subscriptionService } from '@/services/subscription'
import { usersService } from '@/services/users'
import type { User, UserSubscription } from '@/types/user'
import { useQueryClient } from '@tanstack/react-query'
import { FileText, Info } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

interface ProfileCardProps {
  user: User
}

export function ProfileCard({ user }: ProfileCardProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [actionLoading, setActionLoading] = useState(false)
  const [editPasswordOpen, setEditPasswordOpen] = useState(false)
  const [disable2FAOpen, setDisable2FAOpen] = useState(false)

  const handlePasswordUpdated = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.me })
    toast.success(t('profile.editPassword.success'))
  }

  const handle2FADisabled = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.me })
    toast.success(t('profile.disable2FA.success'))
  }

  const is2FAEnabled = Boolean(user.is_2fa_enabled)
  const fullName = formatFullName(user)
  const identifier = user.rpps || user.finess_number

  const organizationName =
    user.pending_payment?.organization_name ??
    user.subscriptions?.find(s => s.organization_name)?.organization_name

  const hasLicenceContent =
    user.organization_quote != null ||
    !user.is_verified ||
    user.pending_payment != null ||
    (user.subscriptions != null && user.subscriptions.length > 0)

  const getRoleLabel = () => {
    if (user.customer_type === CUSTOMER_TYPE.ORGANIZATION) {
      return t('header.roles.organization')
    }
    switch (user.individual_type) {
      case IndividualType.INTERN:
        return t('header.roles.intern')
      case IndividualType.DOCTOR:
        return t('header.roles.doctor')
      case IndividualType.ADHERENT:
        return t('header.roles.adherent')
      default:
        return '—'
    }
  }

  const handlePaySubscription = async () => {
    if (!user.pending_payment) return
    setActionLoading(true)
    try {
      const customerType =
        user.customer_type === CUSTOMER_TYPE.ORGANIZATION
          ? ('organization' as const)
          : ('user' as const)
      const organizationId =
        customerType === 'organization'
          ? (user.organization_quote?.organization_id ??
            user.pending_payment.organization_id ??
            user.subscriptions?.[0]?.organization_id ??
            undefined)
          : undefined
      const baseUrl = import.meta.env.VITE_FRONTEND_URL ?? window.location.origin
      const response = await subscriptionService.createCheckoutSession({
        price_id: user.pending_payment.price_id,
        customer_type: customerType,
        organization_id: organizationId,
        success_url: `${baseUrl}/payment-success`,
        cancel_url: `${baseUrl}/payment-cancelled`,
      })
      window.location.href = response.url
    } catch {
      setActionLoading(false)
    }
  }

  const handleVerifyAccount = async () => {
    if (!user.email) return
    setActionLoading(true)
    try {
      await usersService.resendVerificationEmail(user.email)
      toast.success(t('profile.licence.verificationEmailSent'))
    } catch {
      toast.error(t('profile.licence.verificationEmailError'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleViewQuote = async () => {
    if (!user.organization_quote) return
    setActionLoading(true)
    try {
      const blob = await organizationsService.getQuotePdf(
        user.organization_quote.organization_id,
        user.organization_quote.id
      )
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch {
      // silently fail
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="border-border bg-card rounded-2xl border p-6">
      <h2 className="text-foreground mb-4 text-base font-semibold">{t('profile.cardTitle')}</h2>

      <div className="space-y-4">
        <div className="border-border bg-card flex items-center gap-4 rounded-lg border p-4">
          <div className="bg-button-hover flex h-[88px] w-[88px] shrink-0 items-center justify-center overflow-hidden rounded-full">
            <img
              src={user.customer_type === CUSTOMER_TYPE.ORGANIZATION ? '/struct.svg' : '/user.png'}
              alt=""
              className="h-[58px] w-[58px] object-contain"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-heading mb-1 text-base font-semibold">{fullName}</p>
            {identifier && (
              <div className="bg-badge text-badge-foreground mb-1 inline-flex rounded-[6px] px-4 py-1 text-sm">
                {identifier}
              </div>
            )}
            <p className="text-label text-sm">{getRoleLabel()}</p>
            {organizationName && <p className="text-label text-sm">{organizationName}</p>}
          </div>
        </div>

        <div className="border-border bg-card flex items-center justify-between rounded-lg border p-4 py-2">
          <div className="min-w-0 flex-1">
            <p className="text-label text-[14px] font-normal">{t('profile.emailLabel')}</p>
            <p className="text-heading text-[16px] font-semibold">
              {user.username ?? user.email ?? '—'}
            </p>
          </div>
        </div>

        <div className="border-border bg-card flex items-center justify-between rounded-lg border p-4 py-2">
          <div className="min-w-0 flex-1">
            <p className="text-label text-[14px] font-normal">{t('profile.passwordLabel')}</p>
            <p className="text-heading text-[16px] font-semibold">{t('profile.passwordMask')}</p>
          </div>
          <button
            type="button"
            onClick={() => setEditPasswordOpen(true)}
            className="border-button-border bg-card text-foreground hover:bg-button-hover my-3 cursor-pointer rounded-lg border px-4 py-2 text-[14px] font-semibold transition-colors"
          >
            {t('profile.modifyButton')}
          </button>
        </div>

        <EditPasswordModal
          open={editPasswordOpen}
          onOpenChange={setEditPasswordOpen}
          onSuccess={handlePasswordUpdated}
        />

        <div className="border-border bg-card flex items-center justify-between rounded-lg border p-4 py-2">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div>
              <p className="text-label text-[14px] font-normal">{t('profile.twoFa.label')}</p>
              <p className="text-heading text-[16px] font-semibold">
                {is2FAEnabled ? t('profile.twoFa.enabled') : t('profile.twoFa.disabled')}
              </p>
            </div>
          </div>
          {is2FAEnabled ? (
            <button
              type="button"
              onClick={() => setDisable2FAOpen(true)}
              className="border-button-border bg-card text-foreground hover:bg-button-hover my-3 cursor-pointer rounded-lg border px-5 py-2 text-[14px] font-semibold transition-colors"
            >
              {t('profile.twoFa.disable')}
            </button>
          ) : (
            <Link
              to={ROUTES.ENABLE_2FA}
              className="border-button-border bg-card text-foreground hover:bg-button-hover my-3 inline-flex cursor-pointer items-center justify-center rounded-lg border px-5 py-2 text-[14px] font-semibold transition-colors"
            >
              {t('profile.twoFa.enable')}
            </Link>
          )}
        </div>

        <Disable2FAModal
          open={disable2FAOpen}
          onOpenChange={setDisable2FAOpen}
          onSuccess={handle2FADisabled}
        />
      </div>

      {hasLicenceContent && (
        <>
          <div className="border-border -mx-6 mt-5 mb-0 border-t" role="separator" />

          <div className="mt-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-heading text-base font-semibold">{t('profile.licence.title')}</h3>
              {user.organization_quote != null && (
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={handleViewQuote}
                  className="bg-info-light text-info inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
                >
                  <FileText className="h-4 w-4" />
                  {t('profile.licence.downloadQuote')}
                </button>
              )}
            </div>

            {!user.is_verified && (
              <div className="bg-info-light mb-4 flex items-center justify-between gap-4 rounded-lg px-4 py-2">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Info className="text-info mt-0.5 h-5 w-5 shrink-0" />
                  <p className="text-foreground text-sm font-normal">
                    {t('profile.licence.unverifiedWarning')}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={handleVerifyAccount}
                  className="bg-info shrink-0 cursor-pointer rounded-lg px-3 py-2.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {t('profile.licence.verifyAccount')}
                </button>
              </div>
            )}

            {user.pending_payment != null && user.is_verified && (
              <div className="bg-info-light mb-4 flex items-center justify-between gap-4 rounded-lg px-4 py-2">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Info className="text-info mt-0.5 h-5 w-5 shrink-0" />
                  <p className="text-foreground text-sm font-normal">
                    {t('profile.licence.pendingWarning')}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={handlePaySubscription}
                  className="bg-info shrink-0 cursor-pointer rounded-lg px-3 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {t('profile.licence.payNow')}
                </button>
              </div>
            )}

            {(user.pending_payment ?? user.subscriptions?.[0]) && (
              <SubscriptionBlock
                subscription={user.pending_payment ?? user.subscriptions[0]}
                isPending={user.pending_payment != null}
                isQuoteRequester={user.organization_quote != null}
                licenseActivated={user.license_activated}
                isOrganization={user.customer_type === CUSTOMER_TYPE.ORGANIZATION}
                t={t}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

function extractSeatCount(productName: string): string | null {
  const match = productName.match(/Pack\s+(\d+)/i)
  return match ? match[1] : null
}

function formatInterval(interval: string | undefined, t: (key: string) => string): string {
  if (interval === 'month') return t('profile.licence.intervalMonthly')
  if (interval === 'year') return t('profile.licence.intervalYearly')
  return '—'
}

function SubscriptionBlock({
  subscription,
  isPending,
  isQuoteRequester,
  licenseActivated,
  isOrganization,
  t,
}: {
  subscription: UserSubscription
  isPending: boolean
  isQuoteRequester: boolean
  licenseActivated?: boolean
  isOrganization: boolean
  t: (key: string) => string
}) {
  const rawAmount = subscription.price?.unit_amount
  const amount = rawAmount != null ? `${rawAmount / 100} ${t('profile.licence.euros')}` : '—'
  const expiration = formatExpiration(subscription.current_period_end ?? null)
  const seatCount = extractSeatCount(subscription.product?.name ?? '')
  const interval = formatInterval(subscription.price?.recurring?.interval, t)

  const isSubscriptionActive =
    subscription.subscription_id != null &&
    (subscription.current_period_start != null || subscription.current_period_end != null)
  const showPendingState = isQuoteRequester
    ? licenseActivated !== true
    : isPending || !isSubscriptionActive

  return (
    <div className="border-border bg-card rounded-2xl border">
      <div className="border-border mb-3 flex items-start justify-between gap-2 border-b p-4">
        <span className="text-heading text-base font-semibold">
          {subscription.product?.name ?? '—'}
        </span>
        <span
          className={`shrink-0 rounded px-3 py-1 text-xs font-medium ${
            showPendingState
              ? 'border-warning bg-warning-light text-warning border'
              : 'border-success bg-success-light text-success border'
          }`}
        >
          {showPendingState
            ? t('profile.licence.statusPending')
            : t('profile.licence.statusActive')}
        </span>
      </div>
      <div className="p-4 pt-0">
        {isOrganization && seatCount && (
          <div className="flex justify-between text-sm">
            <span className="text-label">{t('profile.licence.seats')}</span>
            <span className="text-heading font-semibold">{seatCount}</span>
          </div>
        )}
        <div
          className={`flex justify-between text-sm ${isOrganization && seatCount ? 'mt-1' : ''}`}
        >
          <span className="text-label">{t('profile.licence.amount')}</span>
          <span className="text-heading font-semibold">{amount}</span>
        </div>
        <div className="mt-1 flex justify-between text-sm">
          <span className="text-label">{t('profile.licence.expirationDate')}</span>
          <span className="text-heading font-medium">{expiration}</span>
        </div>
        {interval && (
          <div className="mt-1 flex justify-between text-sm">
            <span className="text-label">{t('profile.licence.period')}</span>
            <span className="text-heading font-medium">{interval}</span>
          </div>
        )}
      </div>
    </div>
  )
}
